import React from "react"
import { graphql } from "gatsby"
import styled from "styled-components"
import { rem, hideVisually } from "polished"
import { Meta, Map } from "@outdoormap/gatsby-plugin-brand-sites"

import Layout from "../components/layout"
import Styled from "../components/styled"
import EventHero from "../components/event-hero"
import Infobox from "../components/infobox"
import CardsCarousel from "../components/cards-carousel"
import SiteCard from "../components/site-card"
import { Label3 } from "../components/styled/label"
import { paragraphLargeStyles } from "../components/styled/paragraph"

export default function EventPostTemplate({ data: { translations, event } }) {
  const SitesComponent = () => {
    return (
      <Sites>
        <Label3
          as="h2"
          dangerouslySetInnerHTML={{
            __html: translations.global.relatedPlaces,
          }}
        />

        <CardsCarousel
          cards={event.sites.map((s) => (
            <SiteCard key={s.id} data={s} />
          ))}
          visibleSlides={2}
        />
      </Sites>
    )
  }

  return (
    <Layout>
      <article>
        <EventHero data={event} />

        <Columns>
          <ColumnMain>
            {event.body && (
              <Content dangerouslySetInnerHTML={{ __html: event.body }} />
            )}

            {!!event.sites?.length && <SitesComponent />}
          </ColumnMain>

          <ColumnAside>
            {!!event.sites?.length && (
              <MapWrap>
                <h2
                  dangerouslySetInnerHTML={{ __html: translations.global.map }}
                />

                <Map
                  query={event.sites.map((s) => `site=${s.id}`).join(`&`)}
                  menu="fullscreen"
                  scrollZoom="false"
                  styleControl="fullscreen"
                  zoomControl="fullscreen"
                  measureControl="fullscreen"
                  locationControl="fullscreen"
                  printControl="fullscreen"
                />
              </MapWrap>
            )}

            <Infobox data={event} />
          </ColumnAside>
        </Columns>

        {!!event.sites?.length && (
          <SitesMobile>
            <SitesComponent />
          </SitesMobile>
        )}
      </article>
    </Layout>
  )
}

export const Head = ({ data: { event } }) => {
  return (
    <Meta
      data={{
        title: event.title,
        socialImage: event.images[0]?.images.fallback.src,
      }}
    />
  )
}

export const pageQuery = graphql`
  query ($id: String!) {
    translations: translationsJson {
      global {
        map
        relatedPlaces
      }
    }

    event: eventsJson(id: { eq: $id }) {
      title
      body
      openTimes: open_times {
        opensIso: opens
        opens(formatString: "YYYY-MM-DD · HH:mm")
        closes(formatString: "HH:mm")
      }
      links {
        id
        url
        text
      }
      files {
        id
        url
        text
      }
      images {
        ...NKimageFragment
      }
      sites {
        ...SitesCardFragment
      }
    }
  }
`

const Sites = styled.section`
  margin-top: ${rem(40)};
  padding-top: ${rem(40)};
  border-top: 1px solid ${({ theme }) => theme.colors.cream};

  @media ${({ theme }) => theme.mq.mediumDown} {
    margin-top: ${rem(20)};
    padding-top: ${rem(20)};
  }

  ${Label3} {
    margin-bottom: ${rem(20)};
    color: ${({ theme }) => theme.colors.gold};
  }
`

const Columns = styled.div`
  ${({ theme }) => theme.grid.container(`narrow`)}
  ${({ theme }) => theme.grid.grid()}
  ${({ theme }) => theme.grid.gridGutterX()}

  margin-top: ${rem(50)};
  margin-bottom: ${rem(50)};

  @media ${({ theme }) => theme.mq.mediumDown} {
    margin-bottom: ${rem(20)};
  }
`

const ColumnMain = styled.div`
  ${({ theme }) => theme.grid.cell(8)}

  padding-right: 10%;

  @media ${({ theme }) => theme.mq.mediumDown} {
    ${({ theme }) => theme.grid.cell(12)}
  }

  ${Sites} {
    @media ${({ theme }) => theme.mq.mediumDown} {
      display: none;
    }
  }
`

const ColumnAside = styled.aside`
  ${({ theme }) => theme.grid.cell(4)}

  @media ${({ theme }) => theme.mq.mediumDown} {
    ${({ theme }) => theme.grid.cell(12)}

    margin-top: ${rem(30)};
  }
`

const Content = styled(Styled)`
  p:first-child {
    ${paragraphLargeStyles}
  }

  > *:not(:first-child) {
    max-width: ${rem(520)};
  }
`

const MapWrap = styled.section`
  min-height: ${rem(400)};
  height: ${rem(600)};
  margin-bottom: ${rem(10)};
  overflow: hidden;
  border-radius: ${rem(5)};

  > h2 {
    ${hideVisually()}
  }
`

const SitesMobile = styled.footer`
  ${({ theme }) => theme.grid.container(`narrow`)}

  margin-bottom: ${rem(40)};
  display: none;

  @media ${({ theme }) => theme.mq.mediumDown} {
    display: block;
  }
`
